import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────────────
 * FeatureAttributePopup
 *
 * The standard feature panel: a title, the actions this feature supports, and
 * its attributes as a plain label/value table.
 *
 * Deliberately quiet. This card sits over a map, on top of the user's data, and
 * everything in it competes with that data for attention — so it has one text
 * size for labels, one for values, one accent colour, and no chrome that is not
 * doing a job. The earlier version carried a tab bar whose tabs opened stub
 * panels and a "Field / Value" header row with a filter button that filtered
 * nothing; both are gone rather than restyled.
 *
 * Self-contained: no external state, no data fetching — pass it `fields` and it
 * renders. See the usage example at the bottom.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface AttributeField {
  field: string;
  value: string | number | null | undefined;
}

export interface PopupMetadataEntry {
  label: string;
  value: string;
}

/**
 * Paging between features found at one click.
 *
 * Lives inside the card rather than in a bar beside it: a click on a dense
 * utility map can hit twenty features, and a separate strip listing them all
 * took more of the map than the attributes did.
 */
export interface PopupPager {
  /** Zero-based. */
  index: number;
  total: number;
  /** Which layer the current feature came from. */
  label?: string;
  onPrevious: () => void;
  onNext: () => void;
}

export interface FeatureAttributePopupProps {
  /** The heading — a layer name, or whatever the pop-up's title template built. */
  title: string;
  fields: AttributeField[];
  /**
   * Footer metadata. Only entries present here are rendered, so a service that
   * publishes no editor-tracking fields simply gets no footer.
   */
  metadata?: PopupMetadataEntry[];
  /** Rows shown before "Show more" is expanded. */
  collapsedRowCount?: number;
  /** Caps the table height so long attribute lists scroll instead of overflowing. */
  maxTableHeight?: number;
  /** `compact` shrinks the whole card for floating over a map. */
  size?: 'default' | 'compact';
  /**
   * Whether the caller currently has the panel expanded.
   *
   * The panel does not resize itself — the host owns its geometry (the
   * wrapper div's own `width` style, driven by this same flag). This flag
   * only tells the header which way the expand control points, so the
   * button reflects what pressing it will do.
   */
  expanded?: boolean;
  pager?: PopupPager;
  onClose?: () => void;
  /** Omit and the expand control is not rendered — no control that does nothing. */
  onExpand?: () => void;
  /**
   * Starts a drag from the header — omitted, the card is not draggable at
   * all, which is what the pop-up-configuration PREVIEW (`PopupProperties`)
   * wants: nothing there needs to move, and there is no map underneath to
   * float over. The host owns what "dragged" even means (usually a transform
   * on whatever wraps this card), which is why this only reports the pointer
   * event rather than the card moving itself.
   */
  onHeaderPointerDown?: (event: React.PointerEvent<HTMLElement>) => void;
  /** Omit and the Zoom to action is not rendered, for the same reason. */
  onZoomTo?: () => void;
  className?: string;
}

const EMPTY_VALUE = '–';

/**
 * Two sizes, one layout.
 *
 * Everything that changes between them is here, so the markup below never
 * branches on size and the two variants cannot drift apart.
 */
const SIZES = {
  default: {
    headerPad: 'px-4 py-3',
    title: 'text-[15px]',
    iconButton: 'h-8 w-8',
    iconSize: 'h-4 w-4',
    iconGap: 'gap-1',
    actionPad: 'px-4 py-2',
    actionText: 'text-[13px]',
    cellPad: 'px-3.5 py-2.5',
    cellText: 'text-[13.5px]',
    footPad: 'px-4 py-2',
    footText: 'text-[11px]',
    metaPad: 'px-4 py-2.5',
    metaLabel: 'text-[9.5px]',
    metaValue: 'text-[12px]',
  },
  compact: {
    headerPad: 'px-3 py-2.5',
    title: 'text-[13.5px]',
    iconButton: 'h-6 w-6',
    iconSize: 'h-3.5 w-3.5',
    iconGap: 'gap-0.5',
    actionPad: 'px-3 py-1.5',
    actionText: 'text-[12px]',
    cellPad: 'px-3 py-2',
    cellText: 'text-[12.5px]',
    footPad: 'px-3 py-1.5',
    footText: 'text-[10.5px]',
    metaPad: 'px-3 py-2',
    metaLabel: 'text-[9px]',
    metaValue: 'text-[11px]',
  },
} as const;

type SizeTokens = (typeof SIZES)['default'] | (typeof SIZES)['compact'];

/** Treats null, undefined, and whitespace-only strings as empty. */
function formatValue(value: AttributeField['value']): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  const asString = String(value).trim();
  return asString === '' ? EMPTY_VALUE : asString;
}

function HeaderIconButton({
  label,
  onClick,
  tokens,
  children,
}: {
  label: string;
  onClick?: () => void;
  tokens: SizeTokens;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex ${tokens.iconButton} flex-shrink-0 items-center justify-center rounded-md text-[var(--popup-quiet)] transition hover:bg-[var(--popup-hover)] hover:text-[var(--popup-title)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--popup-accent)]/40`}
    >
      {children}
    </button>
  );
}

export default function FeatureAttributePopup({
  title,
  fields,
  metadata = [],
  collapsedRowCount = 6,
  expanded = false,
  maxTableHeight = 320,
  size = 'default',
  pager,
  onClose,
  onExpand,
  onHeaderPointerDown,
  onZoomTo,
  className = '',
}: FeatureAttributePopupProps) {
  const t = SIZES[size];
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const visibleFields = useMemo(
    () => (showAll ? fields : fields.slice(0, collapsedRowCount)),
    [fields, showAll, collapsedRowCount],
  );

  const hasHiddenRows = fields.length > collapsedRowCount;

  return (
    <section
      role="dialog"
      aria-label={title}
      /* `self-start` keeps the panel its natural height when dropped into a
         flex container, which would otherwise stretch it to full height. */
      className={`gl-popup gl-surface--over-map w-full self-start overflow-hidden ${className}`}
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
        background: 'var(--popup-bg)',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <header
        className={`flex items-start gap-2 border-b border-[var(--popup-line)] ${t.headerPad}`}
        onPointerDown={onHeaderPointerDown}
        style={onHeaderPointerDown ? { cursor: 'move', userSelect: 'none' } : undefined}
      >
        <h2 className={`min-w-0 flex-1 ${t.title} font-semibold leading-snug text-[var(--popup-title)]`}>
          {title}
        </h2>
        <div className={`flex flex-shrink-0 items-center ${t.iconGap}`}>
          {onExpand && (
            <HeaderIconButton
              label={expanded ? 'Shrink panel' : 'Expand panel'}
              onClick={onExpand}
              tokens={t}
            >
              {expanded ? (
                <Minimize2 className={`${t.iconSize} rotate-90`} />
              ) : (
                <Maximize2 className={`${t.iconSize} rotate-90`} />
              )}
            </HeaderIconButton>
          )}
          <HeaderIconButton
            label={collapsed ? 'Show attributes' : 'Hide attributes'}
            onClick={() => setCollapsed((current) => !current)}
            tokens={t}
          >
            <ChevronDown
              className={`${t.iconSize} transition-transform ${collapsed ? '-rotate-90' : ''}`}
            />
          </HeaderIconButton>
          {onClose && (
            <HeaderIconButton label="Close" onClick={onClose} tokens={t}>
              <X className={t.iconSize} />
            </HeaderIconButton>
          )}
        </div>
      </header>

      {!collapsed && (
        <>
          {/* ── Actions ──────────────────────────────────────────
              Only what the host actually wired. An empty row is not
              rendered at all, rather than shown as a bar of nothing. */}
          {onZoomTo && (
            <div className={`border-b border-[var(--popup-line)] ${t.actionPad}`}>
              <button
                type="button"
                onClick={onZoomTo}
                className={`inline-flex items-center gap-1.5 rounded ${t.actionText} font-medium text-[var(--popup-accent)] transition hover:text-[var(--popup-title)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--popup-accent)]/40`}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={t.iconSize}
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5M11 8v6M8 11h6" />
                </svg>
                Zoom to
              </button>
            </div>
          )}

          {/* ── Attributes ───────────────────────────────────────
              A label column and a value column, nothing else, striped the way
              Esri's popup stripes: alternate rows carry the tint so a row reads
              across as one band. The earlier version tinted the label COLUMN
              instead, which drew a hard vertical edge down the middle of the
              card and made the two halves read as separate lists.

              Neither column is emphasised. The label is the quiet half and the
              value the one being read, so the value is darker — not bolder. */}
          <div className="overflow-auto" style={{ maxHeight: maxTableHeight }}>
            <table className="w-full table-fixed border-collapse text-left">
              <tbody>
                {visibleFields.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-[var(--popup-quiet)]">
                      No attributes available for this feature.
                    </td>
                  </tr>
                ) : (
                  visibleFields.map((row, index) => (
                    <tr
                      key={`${row.field}-${index}`}
                      className={index % 2 === 1 ? 'bg-[var(--popup-stripe)]' : 'bg-transparent'}
                    >
                      <th
                        scope="row"
                        className={`w-[40%] ${t.cellPad} align-top text-left ${t.cellText} font-normal leading-snug text-[var(--popup-label)]`}
                      >
                        {row.field}
                      </th>
                      {/* `break-words` because a field can hold a URL or an
                          unbroken id, and table-fixed would otherwise let it
                          push the column past the card. */}
                      <td
                        className={`${t.cellPad} align-top ${t.cellText} font-normal leading-snug text-[var(--popup-value)] break-words`}
                      >
                        {formatValue(row.value)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {hasHiddenRows && (
            <button
              type="button"
              onClick={() => setShowAll((current) => !current)}
              aria-expanded={showAll}
              className={`flex w-full items-center justify-center gap-1.5 border-b border-[var(--popup-line)] ${t.footPad} ${t.footText} font-medium text-[var(--popup-accent)] transition hover:bg-[var(--popup-hover)]`}
            >
              {showAll ? 'Show less' : `Show all ${fields.length} fields`}
              <ChevronDown
                className={`h-3 w-3 transition-transform ${showAll ? 'rotate-180' : ''}`}
              />
            </button>
          )}

          {/* ── Feature pager ────────────────────────────────────
              Part of the card, not a second floating thing beside it. */}
          {pager && pager.total > 1 && (
            <nav
              aria-label="Features at this location"
              className={`flex items-center gap-1 border-b border-[var(--popup-line)] bg-[var(--popup-stripe)] ${t.footPad}`}
            >
              <button
                type="button"
                onClick={pager.onPrevious}
                disabled={pager.index === 0}
                aria-label="Previous feature"
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[var(--popup-title)] transition hover:bg-[var(--popup-hover)] disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <p
                className={`min-w-0 flex-1 truncate text-center ${t.footText} text-[var(--popup-quiet)]`}
                title={pager.label}
              >
                <span className="font-semibold tabular-nums text-[var(--popup-title)]">
                  {pager.index + 1} of {pager.total}
                </span>
                {pager.label ? <span className="ml-1.5">· {pager.label}</span> : null}
              </p>
              <button
                type="button"
                onClick={pager.onNext}
                disabled={pager.index === pager.total - 1}
                aria-label="Next feature"
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[var(--popup-title)] transition hover:bg-[var(--popup-hover)] disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </nav>
          )}

          {/* ── Footer metadata ──────────────────────────────────
              Rendered only for metadata the service actually returned; a
              source with no editor-tracking fields gets no footer at all. */}
          {metadata.length > 0 && (
            <footer className={`flex flex-wrap gap-x-5 gap-y-1.5 bg-[var(--popup-stripe)] ${t.metaPad}`}>
              {metadata.map((entry) => (
                <div key={entry.label} className="min-w-0">
                  <p
                    className={`${t.metaLabel} font-medium uppercase tracking-[0.08em] text-[var(--popup-quiet)]`}
                  >
                    {entry.label}
                  </p>
                  {/* Wraps rather than truncates — a clipped timestamp
                      ("12 May 2024, 10:…") is worse than a second line. */}
                  <p className={`break-words ${t.metaValue} font-medium leading-snug text-[var(--popup-value)]`}>
                    {entry.value === '' ? EMPTY_VALUE : entry.value}
                  </p>
                </div>
              ))}
            </footer>
          )}
        </>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * USAGE EXAMPLE
 *
 * import FeatureAttributePopup, { AttributeField } from './components/FeatureAttributePopup';
 *
 * const projectFields: AttributeField[] = [
 *   { field: 'Capital Program',   value: 'Storm System Improvements' },
 *   { field: 'Organization',      value: 'Engineering Services' },
 *   { field: 'Program Subclass',  value: 'Drainage Improvements' },
 *   { field: 'Project Status',    value: 'Under Construction' },
 *   { field: 'Project Url',       value: null },
 * ];
 *
 * <FeatureAttributePopup
 *   title="Polygon - Capital Projects"
 *   fields={projectFields}
 *   metadata={[{ label: 'Object ID', value: '12465' }]}
 *   size="compact"
 *   pager={{ index: 2, total: 20, label: 'ga_boundary', onPrevious: prev, onNext: next }}
 *   onClose={() => setOpen(false)}
 *   onExpand={() => setExpanded((value) => !value)}
 * />
 * ──────────────────────────────────────────────────────────────────────────── */
